import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { 
  Menu, X, Sun, Moon, ChevronDown, BookOpen, Zap, Award, 
  Flame, Package, Search, ShieldCheck, Store, Car, Building2, 
  Headphones, LayoutDashboard, Pencil, LogOut, Home
} from "lucide-react";

import logoDark from "../assets/logos/brand-logo-dark.png";
import logoLight from "../assets/logos/brand-logo-light.png";

// ─── DATA & CONFIG ────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { key: "home", label: "Home", icon: <Home className="w-4 h-4" />, path: "/" },
  { key: "marketplace", label: "Marketplace", icon: <Store className="w-4 h-4" />, path: "/marketplace" },
  {
    key: "load-board",
    label: "Load Board",
    icon: <Search className="w-4 h-4" />,
    path: "/load-board",
    megaMenu: "load-board",
    dropdown: [
      { key: "find-load", label: "Find a Load", icon: "🔍", path: "/load-board" },
      { key: "post-load", label: "Post a Load", icon: "📦", path: "/post-load" },
    ]
  },
  { 
    key: "training-hub", 
    label: "Training", 
    icon: <BookOpen className="w-4 h-4" />, 
    path: "/training",
    megaMenu: "training",
    dropdown: [
      { key: "core", label: "Core Learning", icon: "📖", path: "/training?tab=courses" },
      { key: "fast-track", label: "Fast-Track Courses", icon: "⚡", path: "/training?tab=courses" },
      { key: "exams", label: "Certification & Exams", icon: "🏅", path: "/training?tab=how" },
      { key: "crash-course", label: "Crash Course", icon: "🔥", path: "/training/ppca-all-states-crash-course" },
    ]
  },
  { key: "verified", label: "Get Verified", icon: <Award className="w-4 h-4" />, path: "/pilot-user-verification-badge" },
  { key: "pricing", label: "Pricing", icon: <ShieldCheck className="w-4 h-4" />, path: "/pricing" },
];

const LOGIN_ROUTES = [
  { id: "driver",     label: "Driver",     icon: Car,        path: "/login" },
  { id: "company",    label: "Company",    icon: Building2,  path: "/company-login" },
  { id: "dispatcher", label: "Dispatcher", icon: Headphones, path: "/dispatcher-login" },
];

function safeUser() {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch {
    return null;
  }
}

function handleLogout() {
  // Completely remove the session keys
  localStorage.removeItem("user");
  localStorage.removeItem("token");
  localStorage.removeItem("authExpiry");
  
  window.location.reload();
}

// ─── MOBILE DRAWER ────────────────────────────────────────────────────────────
function MobileDrawer({ open, close, path, navigate }) {
  if (!open) return null;
  const user = safeUser();
  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "U";
    
  const go = (p) => { navigate(p); close(); };

  return (
    <div className="fixed inset-0 z-[1000] flex lg:hidden">
      {/* Backdrop */}
      <div
        onClick={close}
        className="flex-1 bg-slate-900/60 backdrop-blur-sm animate-[fadeIn_.2s_ease]"
      />
      {/* Drawer */}
      <div className="w-[300px] max-w-[85vw] h-full flex flex-col overflow-y-auto bg-white dark:bg-gradient-to-b dark:from-[#0d1f3c] dark:to-[#1a3358] shadow-2xl animate-[slideRight_.25s_cubic-bezier(.22,.68,0,1.1)_both]">
        
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-6 pb-5 border-b border-slate-200 dark:border-white/10">
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <div className="h-11 w-11 rounded-full bg-gradient-to-br from-violet-600 to-amber-500 flex items-center justify-center font-extrabold text-[15px] text-white shadow-md">
                  {initials}
                </div>
                <div>
                  <div className="text-[15px] font-bold text-slate-900 dark:text-white">{user.name || "Driver"}</div>
                  <div className="text-[12px] font-medium text-slate-500 dark:text-white/60">{user.roleLabel || "PilotLink Member"}</div>
                </div>
              </>
            ) : (
              <>
                <div className="h-11 w-11 rounded-xl bg-slate-100 dark:bg-white/10 border border-slate-200 dark:border-white/15 flex items-center justify-center text-xl shadow-sm">👋</div>
                <div>
                  <div className="text-[15px] font-bold text-slate-900 dark:text-white">Welcome!</div>
                  <div className="text-[12px] font-medium text-slate-500 dark:text-white/60">Join PilotLink USA</div>
                </div>
              </>
            )}
          </div>
          <button
            onClick={close}
            className="h-8 w-8 rounded-full bg-slate-100 dark:bg-white/10 flex items-center justify-center text-slate-600 dark:text-white hover:bg-slate-200 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Navigation Links */}
        <div className="px-3 py-4 flex-1">
          {NAV_ITEMS.map((item) => {
            const active = path === item.path || (item.dropdown && item.dropdown.some(d => d.path === path));
            return (
              <div key={item.key} className="flex flex-col mb-1">
                <button
                  onClick={() => go(item.path)}
                  className={`w-full flex items-center gap-3.5 text-left px-4 py-3 rounded-xl transition-all
                  ${active
                    ? "bg-amber-50/80 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 font-bold"
                    : "text-slate-600 dark:text-white/80 font-semibold hover:bg-slate-50 dark:hover:bg-white/5"}`}
                >
                  <span className={active ? "text-amber-500" : "text-slate-400 dark:text-white/50"}>{item.icon}</span>
                  <span className="text-[14.5px]">{item.label}</span>
                </button>
                
                {/* Mobile Sub-items */}
                {item.dropdown && (
                  <div className="ml-9 mt-1 mb-2 flex flex-col gap-1 border-l-2 border-slate-100 dark:border-white/10 pl-3">
                    {item.dropdown.map((drop) => {
                      const dropActive = path === drop.path;
                      return (
                        <button
                          key={drop.key}
                          onClick={() => go(drop.path)}
                          className={`flex items-center gap-3 text-left px-3 py-2.5 rounded-lg text-[13.5px] transition-colors
                          ${dropActive 
                            ? "text-amber-600 dark:text-amber-400 font-bold bg-amber-50/50 dark:bg-amber-500/5" 
                            : "text-slate-500 dark:text-white/60 font-medium hover:text-slate-800 dark:hover:text-white"}`}
                        >
                          <span>{drop.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Auth Footer */}
        <div className="px-5 pt-4 pb-8 border-t border-slate-200 dark:border-white/10 flex flex-col gap-3 bg-slate-50 dark:bg-white/5">
          {user ? (
            <>
              <div className="text-[11px] font-extrabold text-slate-400 dark:text-white/40 tracking-wider mb-1">MY ACCOUNT</div>
              <button onClick={() => go("/user-dashboard")} className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-white dark:bg-[#1a3358] border border-slate-200 dark:border-white/10 text-slate-800 dark:text-white font-bold text-[14px] shadow-sm">
                <LayoutDashboard className="w-4 h-4 text-blue-600" /> Dashboard
              </button>
              <button onClick={() => go("/profile/edit")} className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-white dark:bg-[#1a3358] border border-slate-200 dark:border-white/10 text-slate-800 dark:text-white font-bold text-[14px] shadow-sm">
                <Pencil className="w-4 h-4 text-amber-500" /> Edit Profile
              </button>
              <button onClick={handleLogout} className="flex items-center justify-center gap-2 w-full py-3 mt-2 rounded-xl border border-rose-200 dark:border-rose-500/30 text-rose-600 dark:text-rose-400 font-bold text-[14px] bg-rose-50 dark:bg-transparent">
                <LogOut className="w-4 h-4" /> Sign Out
              </button>
            </>
          ) : (
            <>
              <div className="text-[11px] font-extrabold text-slate-400 dark:text-white/40 tracking-wider mb-1">LOG IN AS</div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                {LOGIN_ROUTES.slice(0, 2).map(r => {
                  const Icon = r.icon;
                  return (
                    <button key={r.id} onClick={() => go(r.path)} className="flex items-center justify-center gap-2 py-3 rounded-xl bg-white dark:bg-white/10 border border-slate-200 dark:border-white/5 text-slate-700 dark:text-white font-bold text-[13px] shadow-sm">
                      <Icon className="w-4 h-4 opacity-70" /> {r.label}
                    </button>
                  );
                })}
              </div>

              {/* Explicit assignment fixes the syntax issue */}
              {(() => {
                const DispatcherIcon = LOGIN_ROUTES[2].icon;
                return (
                  <button onClick={() => go(LOGIN_ROUTES[2].path)} className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-white dark:bg-white/10 border border-slate-200 dark:border-white/5 text-slate-700 dark:text-white font-bold text-[13px] shadow-sm mb-2">
                    <DispatcherIcon className="w-4 h-4 opacity-70" /> {LOGIN_ROUTES[2].label}
                  </button>
                );
              })()}

              <button
                onClick={() => go("/signup")}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 text-[#0d1f3c] font-extrabold text-[15px] shadow-lg shadow-amber-500/25"
              >
                Join Us Today →
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN NAVBAR ──────────────────────────────────────────────────────────────
export default function Navbar({ isDarkMode, toggleTheme }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const profileRef = useRef(null);

  const user = safeUser();
  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "U";

  // Scroll effect for glassmorphism
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 10);
    s();
    window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  // Close profile dropdown on outside click
  useEffect(() => {
    const h = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  // Handle Resize
  useEffect(() => {
    const r = () => { if (window.innerWidth >= 1024) setDrawerOpen(false); };
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(-6px);} to { opacity:1; transform:none; } }
        @keyframes slideRight { from { transform:translateX(-100%); } to { transform:none; } }
      `}</style>

      <nav
        className={`sticky top-0 z-[500] font-sans transition-all duration-300
        ${scrolled 
          ? "bg-white/90 dark:bg-[#0d1f3c]/90 backdrop-blur-md shadow-lg shadow-slate-900/5 dark:shadow-black/40 border-b border-slate-200/50 dark:border-white/10" 
          : "bg-white dark:bg-[#0d1f3c] border-b border-slate-200 dark:border-white/10"}`}
      >
        <div className="max-w-[1280px] mx-auto h-[68px] px-4 sm:px-6 flex items-center justify-between">

          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 no-underline">
            <img
              src={isDarkMode ? logoDark : logoLight}
              alt="PilotLink USA Logo"
              className="h-8 sm:h-9 w-auto object-contain transition-transform hover:scale-105 duration-200"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1.5 relative ml-8">
            {NAV_ITEMS.map((item) => {
              const active = pathname === item.path || (item.dropdown && item.dropdown.some(d => d.path === pathname));
              const hasDropdown = Boolean(item.dropdown);

              return (
                <div key={item.key} className="relative group">
                  <Link
                    to={item.path}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13.5px] whitespace-nowrap transition-all no-underline
                    ${active
                      ? "font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-400/10"
                      : "font-semibold text-slate-600 dark:text-white/80 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white"}`}
                  >
                    {item.label}
                    {hasDropdown && (
                      <ChevronDown className="h-3 w-3 opacity-60 group-hover:rotate-180 transition-transform duration-200" />
                    )}
                  </Link>

                  {/* Mega Menu: Training */}
                  {hasDropdown && item.megaMenu === "training" && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[200]">
                      <div className="w-[420px] bg-white dark:bg-[#1a3358] border border-slate-200 dark:border-white/15 rounded-2xl shadow-xl shadow-slate-900/10 p-5 flex flex-col gap-1">
                        <div className="text-[11px] font-extrabold text-slate-400 dark:text-slate-400 uppercase tracking-wider mb-2 px-2">How Training Works</div>

                        <Link to="/training?tab=courses" className="group/item flex items-start gap-4 p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5 transition-colors no-underline">
                          <div className="bg-emerald-50 dark:bg-emerald-500/10 p-2.5 rounded-xl text-emerald-600 dark:text-emerald-400 shrink-0 shadow-sm transition-colors">
                            <BookOpen className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 text-[14.5px] font-bold text-slate-900 dark:text-white">
                              Core Learning
                              <span className="bg-emerald-500 text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded-full uppercase tracking-wider">Free</span>
                            </div>
                            <div className="text-[12.5px] text-slate-500 dark:text-white/60 mt-0.5 leading-snug">Foundational modules everyone can start today.</div>
                          </div>
                        </Link>

                        <Link to="/training?tab=courses" className="group/item flex items-start gap-4 p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5 transition-colors no-underline">
                          <div className="bg-amber-50 dark:bg-amber-500/10 p-2.5 rounded-xl text-amber-600 dark:text-amber-400 shrink-0 shadow-sm transition-colors">
                            <Zap className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 text-[14.5px] font-bold text-slate-900 dark:text-white">
                              Fast-Track Courses
                              <span className="bg-amber-500 text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded-full uppercase tracking-wider">Paid</span>
                            </div>
                            <div className="text-[12.5px] text-slate-500 dark:text-white/60 mt-0.5 leading-snug">Accelerated, exam-focused courses.</div>
                          </div>
                        </Link>

                        <Link to="/training?tab=how" className="group/item flex items-start gap-4 p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5 transition-colors no-underline">
                          <div className="bg-blue-50 dark:bg-blue-500/10 p-2.5 rounded-xl text-blue-600 dark:text-blue-400 shrink-0 shadow-sm transition-colors">
                            <Award className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 text-[14.5px] font-bold text-slate-900 dark:text-white">
                              Certification & Exams
                            </div>
                            <div className="text-[12.5px] text-slate-500 dark:text-white/60 mt-0.5 leading-snug">Practice exams and the Verified Badge.</div>
                          </div>
                        </Link>

                        <div className="h-px bg-slate-100 dark:bg-white/10 my-2" />

                        <Link to="/training/ppca-all-states-crash-course" className="group/item flex items-center gap-3.5 p-3.5 rounded-xl bg-amber-50/50 border border-amber-200/50 hover:bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/20 dark:hover:bg-amber-500/20 transition-colors no-underline">
                          <Flame className="w-5 h-5 text-amber-500 shrink-0" />
                          <div>
                            <div className="text-[13.5px] font-bold text-slate-900 dark:text-white">PPCA All-States Crash Course</div>
                            <div className="text-[12px] text-amber-700/80 dark:text-amber-200/80 leading-snug">Our most popular fast-track.</div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* Mega Menu: Load Board */}
                  {hasDropdown && item.megaMenu === "load-board" && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[200]">
                      <div className="w-[360px] bg-white dark:bg-[#1a3358] border border-slate-200 dark:border-white/15 rounded-2xl shadow-xl shadow-slate-900/10 p-3 flex flex-col gap-1">
                        
                        <Link to="/load-board" className="group/item flex items-start gap-4 p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5 transition-colors no-underline">
                          <div className="bg-slate-100 dark:bg-white/10 p-2.5 rounded-xl text-slate-700 dark:text-slate-200 shrink-0 shadow-sm transition-colors">
                            <Search className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 text-[14.5px] font-bold text-slate-900 dark:text-white">
                              Find a Load
                              <span className="bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300 text-[9px] font-bold px-2 py-0.5 rounded-full uppercase">Drivers</span>
                            </div>
                            <div className="text-[12.5px] text-slate-500 dark:text-white/60 mt-0.5 leading-snug">Browse live oversize loads near you.</div>
                          </div>
                        </Link>

                        <Link to="/post-load" className="group/item flex items-start gap-4 p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5 transition-colors no-underline">
                          <div className="bg-slate-100 dark:bg-white/10 p-2.5 rounded-xl text-slate-700 dark:text-slate-200 shrink-0 shadow-sm transition-colors">
                            <Package className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 text-[14.5px] font-bold text-slate-900 dark:text-white">
                              Post a Load
                              <span className="bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300 text-[9px] font-bold px-2 py-0.5 rounded-full uppercase">Companies</span>
                            </div>
                            <div className="text-[12.5px] text-slate-500 dark:text-white/60 mt-0.5 leading-snug">Reach verified, certified drivers.</div>
                          </div>
                        </Link>
                        
                      </div>
                    </div>
                  )}

                </div>
              );
            })}
          </div>

          {/* Right Controls */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0 ml-auto">
            
            {/* ONLY SHOW LOGIN/JOIN IF NO USER */}
            {!user && (
              <div className="hidden lg:flex items-center gap-3">
                {/* Driver / Company / Dispatcher Pill */}
                <div className="flex items-center bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-full p-1 shadow-sm">
                  {LOGIN_ROUTES.map((route) => {
                    const Icon = route.icon; // Explicit assignment fixes desktop nav as well
                    return (
                      <Link
                        key={route.id}
                        to={route.path}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-bold text-slate-600 dark:text-white/80 hover:bg-white dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white transition-all no-underline"
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {route.label}
                      </Link>
                    );
                  })}
                </div>

                <Link
                  to="/signup"
                  className="bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-[#0d1f3c] rounded-full px-5 py-2 text-[13.5px] font-extrabold shadow-md shadow-amber-500/20 transition-all hover:-translate-y-[1px] no-underline"
                >
                  Join Us
                </Link>
              </div>
            )}

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className="h-9 w-9 rounded-xl flex items-center justify-center transition-colors
              bg-slate-100 border border-slate-200 text-slate-600 hover:bg-slate-200
              dark:bg-white/10 dark:border-white/15 dark:text-amber-300 dark:hover:bg-white/15"
            >
              {isDarkMode ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </button>

            {/* ONLY SHOW USER MENU IF USER EXISTS */}
            {user && (
              <div ref={profileRef} className="relative hidden lg:block">
                <button
                  onClick={() => setProfileOpen((p) => !p)}
                  className="h-10 w-10 rounded-full bg-gradient-to-br from-violet-600 to-amber-500 flex items-center justify-center font-extrabold text-[14px] text-white shadow-md border-2 border-white dark:border-[#0d1f3c] transition-transform hover:scale-105"
                >
                  {initials}
                </button>
                
                {profileOpen && (
                  <div className="absolute top-[calc(100%+12px)] right-0 w-[240px] bg-white dark:bg-[#1a3358] border border-slate-200 dark:border-white/15 rounded-2xl shadow-xl shadow-slate-900/10 z-[200] animate-[fadeIn_.15s_ease] overflow-hidden">
                    <div className="px-4 pt-4 pb-3 border-b border-slate-100 dark:border-white/10">
                      <div className="text-[14px] font-extrabold text-slate-900 dark:text-white truncate">{user.name || "Driver"}</div>
                      <div className="text-[12px] font-medium text-slate-500 dark:text-white/60 truncate">{user.roleLabel || "PilotLink Member"}</div>
                    </div>
                    
                    <div className="p-2 flex flex-col gap-1">
                      <button onClick={() => { navigate("/dashboard"); setProfileOpen(false); }} className="flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl text-[13.5px] font-bold text-slate-700 dark:text-white/90 hover:bg-slate-50 dark:hover:bg-white/10 transition-colors">
                        <LayoutDashboard className="w-4 h-4 text-blue-600 dark:text-blue-400" /> Dashboard
                      </button>
                      <button onClick={() => { navigate("/profile/edit"); setProfileOpen(false); }} className="flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl text-[13.5px] font-bold text-slate-700 dark:text-white/90 hover:bg-slate-50 dark:hover:bg-white/10 transition-colors">
                        <Pencil className="w-4 h-4 text-amber-500" /> Edit Profile
                      </button>
                    </div>

                    <div className="p-2 border-t border-slate-100 dark:border-white/10">
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl text-[13.5px] font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
                      >
                        <LogOut className="w-4 h-4" /> Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mobile hamburger */}
            <button
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden h-9 w-9 rounded-xl flex items-center justify-center
              bg-slate-100 border border-slate-200 text-slate-600 hover:bg-slate-200
              dark:bg-white/10 dark:border-white/15 dark:text-white/90 dark:hover:bg-white/15 transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </nav>

      {/* Slide-out Mobile Menu */}
      <MobileDrawer open={drawerOpen} close={() => setDrawerOpen(false)} path={pathname} navigate={navigate} />
    </>
  );
}